import { ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppService, EtScreenRecorderService, UserService, TrainingPlanService, LocalAttachment, CheckinService, OrganizationService, TranslateConfigService } from 'src/app/services';
import { MediaSwitcher } from 'src/app/models';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AlertController } from '@ionic/angular';

@Component({
  selector: 'et-screen-recorder',
  templateUrl: './et-screen-recorder.component.html',
  styleUrls: ['./et-screen-recorder.component.scss'],
})
export class EtScreenRecorderComponent implements OnInit {

  public actionSubscription: Subscription;
  public cameraStarted = false;
  public cameraStream: MediaStream;
  public isConfirm
  public isStarted: boolean = false;
  public isCamera: boolean = false;
  public isCanceled: boolean = false;
  public isPaused: boolean = false;
  public isPencil: boolean = false;
  public mediaStream: MediaStream;
  public mediaSwitcher;
  public mimeType: string | null = null;
  private readonly recorderTimesliceMs = 2000;
  public recorder;
  public recordedChunks = [];
  public recordedVideo: {blob: Blob, url: SafeUrl;} = {blob: null, url: null};
  public screenStream: MediaStream;
  /** True while pointer is over the sharing dock (controls expanded on hover). */
  public sharingActionsExpanded = false;
  /** Keeps the sharing toolbar expanded through mouseup so Cancel still receives the click. */
  public dockPointerDown = false;
  public showBackground: boolean;
  public toSave = false;
  public videoElement: HTMLVideoElement;
  public mainCanvas;
  public recordingSeconds = 0;
  /** True while the finished recording is uploading (in-recorder overlay). */
  public isUploading = false;
  public uploadProgressPercent = 0;
  public uploadStatusMessage = '';

  public userId: string;
  public referenceId: string;
  public referenceType: string;

  private recordingTimer: ReturnType<typeof setInterval> | null = null;
  private pointerOverDock = false;
  private longRecordingWarned = false;
  private lastUploadProgressShown = -1;

  /** Cap capture bitrate so 3–10 min screen+mic videos stay uploadable. */
  private readonly recorderVideoBitsPerSecond = 1_500_000;
  private readonly recorderAudioBitsPerSecond = 96_000;
  /** Soft warning once recording reaches this length (seconds). */
  private readonly longRecordingWarnSeconds = 5 * 60;

  get recordingTimeLabel(): string {
    const minutes = Math.floor(this.recordingSeconds / 60);
    const seconds = this.recordingSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  @ViewChild('liveVideo') videoElementRef: ElementRef;

  @ViewChild('outputVideo') outputVideoElementRef: ElementRef;
  @ViewChild('cameraVideo') cameraVideoElementRef: ElementRef;


  constructor(private alertCtrl: AlertController,
    private appService: AppService,
    private cdr: ChangeDetectorRef,
    private checkinService: CheckinService,
    public etSRService: EtScreenRecorderService,
    private ngZone: NgZone,
    private organizationService: OrganizationService,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private trainingPlanService: TrainingPlanService,
    private translateConfig: TranslateConfigService
  ) { }

  ngOnInit(): void {
    this.actionSubscription = this.etSRService.actionSubject.subscribe((data) => {
      if(data.action === "start") {
        this.isStarted = false;
        this.isCanceled = false;
        this.isCamera = false;
        this.isConfirm = false;
        this.showBackground = true;
        this.recordedChunks = [];
        this.etSRService.enabled = true;
        this.recordedVideo = {blob: null, url: null};
        this.longRecordingWarned = false;
        this.lastUploadProgressShown = -1;
        this.clearUploadUi();
        this.userId = data.userId;
        this.referenceId = data.referenceId;
        this.referenceType = data.referenceType;
        setTimeout(() => {
          this.startCameraStream();
        }, 5);
      }

      if(data.action === "stop") {
        //this.stopRecording();
      }
    });
  }
  
  ngAfterInit() {
    
  }

  async startCameraStream() {    
    //  Initialize camera ----------------------------------------------------------------------
    await this.appService.presentLoading();
    this.initializeCanvas();
   
    await this.initializeCamera();

    this.mediaSwitcher = new MediaSwitcher;
    this.mediaStream = await this.mediaSwitcher.initialize(this.cameraStream)
    const outputVideo = this.outputVideoElementRef.nativeElement as HTMLVideoElement;
    outputVideo.muted = true;
    outputVideo.srcObject = this.mediaStream;
    await this.appService.hideLoading();
    this.initializeRecorder(this.mediaStream);
  }

  private pickSupportedMimeType(): string | null {
    const candidates = [
      // Prefer VP9/VP8 for Chromium; H264-in-WebM is inconsistent across platforms.
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      // Some browsers expose MP4 support; keep as last resort.
      'video/mp4',
    ];

    if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
      return null;
    }

    for (const mime of candidates) {
      try {
        if (MediaRecorder.isTypeSupported(mime)) {
          return mime;
        }
      } catch {
        // ignore
      }
    }
    return null;
  }

  initializeRecorder(stream) {
    this.recordedChunks.length = 0;
    this.mimeType = this.pickSupportedMimeType();

    const recorderOptions: MediaRecorderOptions = {
      videoBitsPerSecond: this.recorderVideoBitsPerSecond,
      audioBitsPerSecond: this.recorderAudioBitsPerSecond,
    };
    if (this.mimeType) {
      recorderOptions.mimeType = this.mimeType;
    }

    try {
      this.recorder = new MediaRecorder(stream, recorderOptions);
    } catch (err) {
      // Fallback if browser rejects bitrate/mime options.
      try {
        this.recorder = this.mimeType
          ? new MediaRecorder(stream, { mimeType: this.mimeType })
          : new MediaRecorder(stream);
      } catch {
        this.mimeType = null;
        this.recorder = new MediaRecorder(stream);
      }
    }

    this.recorder.ondataavailable = (e) => {
      // Some browsers may fire empty chunks; ignore them.
      if (e?.data && e.data.size > 0) {
        this.recordedChunks.push(e.data);
      }
    };

    this.recorder.onstop = async () => {
      try {
        if (!this.toSave) {
          return;
        }

        const blobType = this.mimeType || (this.recordedChunks[0] && this.recordedChunks[0].type) || 'video/webm';
        this.recordedVideo.blob = new Blob(this.recordedChunks, { type: blobType });

        const uploaded = await this.uploadRecordedVideoWithRetry();
        if (!uploaded) {
          return;
        }

        this.etSRService.actionSubject.next({
          action: "finished",
          data: this.recordedVideo.blob,
          userId: this.userId,
          referenceId: this.referenceId
        });
        this.etSRService.enabled = false;
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : this.translateConfig.t('general.videoUploadFailed');
        await this.appService.showError(message);
        this.etSRService.enabled = false;
      } finally {
        this.toSave = null;
        this.clearUploadUi();
      }
    };

    this.recorder.onerror = async () => {
      this.toSave = null;
      this.clearUploadUi();
      this.stopStreams();
    };
  };

  private setUploadUi(message: string, percent = 0) {
    this.ngZone.run(() => {
      this.isUploading = true;
      this.uploadStatusMessage = message;
      this.uploadProgressPercent = percent;
      this.cdr.detectChanges();
    });
  }

  private clearUploadUi() {
    this.ngZone.run(() => {
      this.isUploading = false;
      this.uploadStatusMessage = '';
      this.uploadProgressPercent = 0;
      this.lastUploadProgressShown = -1;
      this.cdr.detectChanges();
    });
  }

  private async uploadRecordedVideoWithRetry(): Promise<boolean> {
    if (!this.recordedVideo?.blob) {
      throw new Error(this.translateConfig.t('general.videoUploadFailed'));
    }

    const tryUpload = async () => {
      this.lastUploadProgressShown = -1;
      this.setUploadUi(this.translateConfig.t('general.uploadingVideo'), 0);
      await this.uploadRecordedVideoAttachment();
    };

    try {
      await tryUpload();
      return true;
    } catch (firstError) {
      this.clearUploadUi();
      const detail =
        firstError instanceof Error && firstError.message
          ? firstError.message
          : this.translateConfig.t('general.videoUploadFailed');

      const shouldRetry = await this.confirmVideoUploadRetry(detail);
      if (!shouldRetry) {
        this.etSRService.enabled = false;
        return false;
      }

      try {
        await tryUpload();
        return true;
      } catch (secondError) {
        const message =
          secondError instanceof Error && secondError.message
            ? secondError.message
            : this.translateConfig.t('general.videoUploadFailed');
        await this.appService.showError(message);
        this.etSRService.enabled = false;
        return false;
      }
    }
  }

  private async confirmVideoUploadRetry(detail: string): Promise<boolean> {
    const alert = await this.alertCtrl.create({
      header: this.translateConfig.t('general.videoUploadFailed'),
      message: `${detail}\n\n${this.translateConfig.t('general.videoUploadRetryPrompt')}`,
      cssClass: 'et-recorder-confirm',
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
        },
        {
          text: this.translateConfig.t('general.retry'),
          role: 'confirm',
        },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  private async uploadRecordedVideoAttachment() {
    const blob = this.recordedVideo.blob;
    const blobType = blob.type || this.mimeType || 'video/webm';
    const extension = blobType.includes('mp4') ? 'mp4' : 'webm';
    const attachmentData: LocalAttachment = {
      name: `${new Date().getTime()}.${extension}`,
      type: 'video',
      file: blob,
    };

    const onProgress = (progress: { percent: number }) => {
      const percent = Math.max(0, Math.min(100, Math.round(progress.percent || 0)));
      if (percent < 100 && percent - this.lastUploadProgressShown < 5) {
        return;
      }
      this.lastUploadProgressShown = percent;
      this.setUploadUi(
        this.translateConfig.t('general.uploadingVideoProgress', { percent }),
        percent
      );
    };

    const activeOrg = this.organizationService.getAppOrganization()?.uid || null;
    if (!this.referenceType || this.referenceType === 'mealPlan') {
      await this.checkinService.addProgressReportCommentAttachment(
        this.userId,
        this.referenceId,
        this.userService.getAppUser(),
        attachmentData,
        activeOrg,
        { onProgress }
      );
    } else if (this.referenceType === 'trackedWorkout') {
      await this.trainingPlanService.addTrackedWorkoutCommentAttachment(
        this.userId,
        this.referenceId,
        this.userService.getAppUser(),
        attachmentData,
        activeOrg,
        { onProgress }
      );
    } else {
      throw new Error(this.translateConfig.t('general.videoUploadFailed'));
    }

    this.setUploadUi(this.translateConfig.t('general.savingVideoComment'), 100);
  }

  // Used to initialize the canvas element and stream
  initializeCanvas() {
    this.mainCanvas = document.createElement("canvas");
    this.mainCanvas.width = 320;
    this.mainCanvas.height = 200;
    const ctx = this.mainCanvas.getContext("2d");
    ctx.fillStyle = "black";
    ctx.strokeStyle = "white";
    ctx.lineWidth = 1;
    ctx.fillRect(0, 0, this.mainCanvas.width, this.mainCanvas.height);

    return
    var loop = 0;
    var step = 1;
    var a, b, c, d;
    setInterval(() => {
      loop = loop + step;
      if (loop > 200 || loop < -200) step = -step;
      ctx.fillRect(0, 0, this.mainCanvas.width, this.mainCanvas.height);

      for (a = 1; a < 639; a += 8) {
        b = loop * Math.sin(a / 60) + 200;
        c = loop * 0.5 * Math.sin(a / 25) + 320;
        d = loop * 0.25 * Math.sin(a / 30) + 50;
        b = Math.round(b / 2);
        c = Math.round(c / 2);
        d = Math.round(d / 2);

        ctx.beginPath();
        ctx.moveTo(Math.round(a / 2), b);
        ctx.lineTo(c, d);
        ctx.stroke();
      }
    }, 1000 / 30);
  }

  initializeCamera() {
    return new Promise((res, rej) => {
      const constraints = {
        video: {
          width: 300,
        },
        audio: {
          autoGainControl: false,
          echoCancellation: false,
          noiseSuppression: false
        }
      };
      navigator.mediaDevices.getUserMedia(constraints)
      .then((stream) => {
        this.cameraStream = stream;

        setTimeout(() => {
          const cameraVideo = this.cameraVideoElementRef.nativeElement as HTMLVideoElement;
          cameraVideo.muted = true;
          cameraVideo.srcObject = this.cameraStream;
          this.isCamera = true;
          // Hack because the ng-image-fullscreen-view plugin is disabling all videos 
          cameraVideo.addEventListener("pause", (event) => {
            if(cameraVideo.paused) {
              setTimeout(() => {
                cameraVideo.play();
              });
            }
          });

          if(this.mediaStream) {
            this.mediaSwitcher.changeTrack(this.cameraStream.getVideoTracks()[0]);
          }

          res(null);
        }, 0);
      })
      .catch((err) => {
        rej(err);
      });
    });
  }

  async enableScreenShare() {
    const constraints = {
      video: true,
      selfBrowserSurface: "include",
      //surfaceSwitching: "include"
    };
    this.screenStream = await navigator.mediaDevices.getDisplayMedia(constraints);
    this.mediaSwitcher.changeTrack(this.screenStream.getVideoTracks()[0]);
    this.showBackground = false;
  }

  disableScreenShare() {
    this.setSourceDefault();
    this.screenStream.getTracks().forEach((track) => {
      track.stop();
    });
    this.screenStream = null;
    this.sharingActionsExpanded = false;
    this.showBackground = true;
  }

  onSharingDockEnter() {
    this.pointerOverDock = true;
    this.sharingActionsExpanded = true;
  }

  onSharingDockLeave() {
    this.pointerOverDock = false;
    if (!this.dockPointerDown) {
      this.sharingActionsExpanded = false;
    }
  }

  toggleSharingActions(event: Event) {
    event.stopPropagation();
    // Click/tap open — hover also expands; do not toggle closed here or the click closes it immediately.
    this.sharingActionsExpanded = true;
  }

  onDockPointerDown() {
    this.dockPointerDown = true;
  }

  @HostListener('window:mouseup')
  onDockPointerUp() {
    if (!this.dockPointerDown) {
      return;
    }
    this.dockPointerDown = false;
    if (!this.pointerOverDock) {
      this.sharingActionsExpanded = false;
    }
  }

  async enableCamera() {
    this.isCamera = true;
    if(!this.screenStream) {
      this.setSourceDefault();
    }
  }

  disableCamera() {
    if(!this.screenStream) {
      this.setSourceCanvas();
    }
    this.isCamera = false;
  }

  setSourceDefault() {
    if(this.cameraStream) {
      this.mediaSwitcher.changeTrack(this.cameraStream.getVideoTracks()[0]);
    } else {
      this.mediaSwitcher.changeTrack(this.mainCanvas.captureStream(30).getVideoTracks()[0]);
    }
  }

  setSourceCanvas() {
    this.mediaSwitcher.changeTrack(this.mainCanvas.captureStream(30).getVideoTracks()[0]);
  }

  playAudio(src: string){
    let audio = new Audio();
    audio.src = src;
    audio.load();
    audio.play();
  }

  startRecording() {
    this.playAudio("assets/audio/recording-started.mp3");
    setTimeout(() => {
      this.ngZone.run(() => {
        this.isStarted = true;
        this.startRecordingTimer();
        this.cdr.detectChanges();
        // Timeslice reduces memory pressure and improves stop reliability.
        this.recorder.start(this.recorderTimesliceMs);
      });
    }, 1500);
  }

  saveRecording()  {
    this.toSave = true;
    // Show upload UI immediately — MediaRecorder.onstop can lag a beat after stop().
    this.setUploadUi(this.translateConfig.t('general.uploadingVideo'), 0);
    try {
      // Best-effort: ask the encoder to flush data before stopping.
      if (typeof this.recorder.requestData === 'function') {
        this.recorder.requestData();
      }
    } catch {
      // ignore
    }

    this.recorder.stop();
    this.stopStreams();
    this.showBackground = true;
    this.isStarted = false;
    this.stopRecordingTimer();
    this.playAudio("assets/audio/recording-ended.mp3");
  };

  pauseRecording() {
    this.isPaused = true;
    this.recorder.pause();
    this.playAudio("assets/audio/recording-paused.mp3");
  }

  resumeRecording() {
    this.isPaused = false;
    this.recorder.resume();
    this.playAudio("assets/audio/recording-resumed.mp3");
  }

  cancelConfirmed() {
    this.isCanceled = true;
    this.toSave = false;
    if(this.isStarted) {
      this.recorder.stop();
    }
    this.stopStreams();
    this.etSRService.enabled = false;
    this.isStarted = false;
    this.stopRecordingTimer();
  }

  private startRecordingTimer() {
    this.stopRecordingTimer();
    this.recordingSeconds = 0;
    this.longRecordingWarned = false;
    this.ngZone.runOutsideAngular(() => {
      this.recordingTimer = setInterval(() => {
        this.ngZone.run(() => {
          if (!this.isPaused) {
            this.recordingSeconds += 1;
            if (
              !this.longRecordingWarned &&
              this.recordingSeconds >= this.longRecordingWarnSeconds
            ) {
              this.longRecordingWarned = true;
              void this.appService.showToast(
                this.translateConfig.t('general.longRecordingUploadWarning')
              );
            }
            this.cdr.detectChanges();
          }
        });
      }, 1000);
    });
  }

  private stopRecordingTimer() {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    this.recordingSeconds = 0;
  }

  async cancelRecording() {
    if(this.isStarted) {
      const alert = await this.alertCtrl.create({
        header: this.translateConfig.t('general.confirm'),
        message: 'Are you sure you want to cancel this recording?',
        cssClass: 'et-recorder-confirm',
        buttons: [
          {
            text: this.translateConfig.t('general.no'),
            role: 'cancel',
            cssClass: 'secondary',
            handler: (blah) => { }
          }, {
            text: this.translateConfig.t('general.yes'),
            handler: async () => {
              this.cancelConfirmed();
            }
          }
        ]
      });
      await alert.present();
    } else {
      this.cancelConfirmed()
    }
  }

  stopStreams() {
    if(this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => {
        track.stop();
      });
      this.mediaStream = null;
    }
    if(this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => {
        track.stop();
      });
      this.cameraStream = null;
    }
    if(this.screenStream) {
      this.screenStream.getTracks().forEach(track => {
        track.stop();
      });
      this.screenStream = null;
    }
    this.sharingActionsExpanded = false;
  }

  initCanvas() {
    // create canvas element and append it to document body
    const container = document.getElementById("EtRecorderCanvas");
    var canvas = document.createElement('canvas');
    container.appendChild(canvas);

    // some hotfixes... ( ≖_≖)
    //document.body.style.margin = 0;
    canvas.style.position = 'absolute';

    // get canvas 2D context and set him correct size
    var ctx = canvas.getContext('2d');
    resize();

    // last known position
    var pos = { x: 0, y: 0 };

    window.addEventListener('resize', resize);
    document.addEventListener('mousemove', draw);
    document.addEventListener('mousedown', setPosition);
    document.addEventListener('mouseenter', setPosition);

    // new position from mouse event
    function setPosition(e) {
      pos.x = e.clientX;
      pos.y = e.clientY;
    }

    // resize canvas
    function resize() {
      ctx.canvas.width = window.innerWidth;
      ctx.canvas.height = window.innerHeight;
    }

    function draw(e) {
      // mouse left button must be pressed
      if (e.buttons !== 1) return;

      ctx.beginPath(); // begin

      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#2dd36f';

      ctx.moveTo(pos.x, pos.y); // from
      setPosition(e);
      ctx.lineTo(pos.x, pos.y); // to

      ctx.stroke(); // draw it!
    }
  }

  togglePencil() {
    this.isPencil = !this.isPencil;
    if(this.isPencil) {
      this.isPencil = true;
      setTimeout(() => {
        this.initCanvas();
      }, 10);
    }
  }

  /*ngOnInit() {
    //this.mimeTypes();

    this.actionSubscription = this.etSRService.actionSubject.subscribe((data) => {
      if(data.action === "start") {
        this.isCanceled = false;
        this.startRecording();
      }

      if(data.action === "stop") {
        this.stopRecording();
      }
    });
  }

  clearMediaStreamTracks() {
    if(this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => {track.stop()})
    }
  }

  clearCameraStreamTracks() {
    if(this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => {track.stop()})
    }
  }

  mimeTypes() {
    function getSupportedMimeTypes(media, types, codecs) {
      const isSupported = MediaRecorder.isTypeSupported;
      const supported = [];
      types.forEach((type) => {
        const mimeType = `${media}/${type}`;
        codecs.forEach((codec) => [
            `${mimeType};codecs=${codec}`,
            `${mimeType};codecs=${codec.toUpperCase()}`,
            // /!\ false positive /!\
            // `${mimeType};codecs:${codec}`,
            // `${mimeType};codecs:${codec.toUpperCase()}` 
          ].forEach(variation => {
            if(isSupported(variation)) 
                supported.push(variation);
        }));
        if (isSupported(mimeType))
          supported.push(mimeType);
      });
      return supported;
    };
    
    // Usage ------------------
    
    const videoTypes = ["webm", "ogg", "mp4", "x-matroska"];
    const audioTypes = ["webm", "ogg", "mp3", "x-matroska"];
    const codecs = ["should-not-be-supported","vp9", "vp9.0", "vp8", "vp8.0", "avc1", "av1", "h265", "h.265", "h264", "h.264", "opus", "pcm", "aac", "mpeg", "mp4a"];
    
    const supportedVideos = getSupportedMimeTypes("video", videoTypes, codecs);
    const supportedAudios = getSupportedMimeTypes("audio", audioTypes, codecs);
    
    console.log('-- Top supported Video : ', supportedVideos[0])
    console.log('-- Top supported Audio : ', supportedAudios[0])
    console.log('-- All supported Videos : ', supportedVideos)
    console.log('-- All supported Audios : ', supportedAudios)
  }

  async startRecording() {
    try {
      const constraints = {
        video: true,
        preferCurrentTab: true,
      };
  
      const videoStream = await navigator.mediaDevices.getDisplayMedia(constraints);
      const audioStream = await navigator.mediaDevices.getUserMedia({audio: true});
  
      this.isStarted = true;
  
      this.mediaStream = new MediaStream([...videoStream.getTracks(), ...audioStream.getTracks()]);
  
      //needed for better browser support      
      const mime = MediaRecorder.isTypeSupported("video/webm; codecs=vp9")? "video/webm; codecs=vp9" : "video/webm"
        
      const mimeType = 'video/webm;codecs=h264';

      this.mediaRecorder = new MediaRecorder(this.mediaStream, {
        mimeType: mimeType,
      })
  
      this.mediaChunks = [];
  
      // Listen for the video stream to explicetely stop the recorder
      this.mediaStream.getVideoTracks()[0].addEventListener("ended", () => {
        this.cancelRecording();
      });
  
      this.mediaRecorder.addEventListener('dataavailable', (e) => {
        this.mediaChunks.push(e.data)
        // Generate Video
        let blob = new Blob(this.mediaChunks, {
          type: mimeType //this.mediaChunks[0].type
        })
        this.etSRService.actionSubject.next({action: this.isCanceled? "canceled" : "finished", data: blob})
      })
  
      this.mediaRecorder.addEventListener('stop', function(){
        console.log("stoped");
      })
  
      //we have to start the recorder manually
      this.mediaRecorder.start()
    } catch (error) {
      console.log("error", error);
    }
  }

  stopRecording() {
    this.isStarted = false;
    this.isPencil = false;
    this.clearMediaStreamTracks();
    this.stopCamera();
  }

  cancelRecording() {
    this.isCanceled = true;
    this.stopRecording();
  }

  async startCamera() {
    this.cameraStream = await navigator.mediaDevices.getUserMedia({video: true});
    this.cameraStarted = true;
    setTimeout(() => {
      this.videoElement = this.videoElementRef.nativeElement;
      this.videoElement.srcObject = this.cameraStream;
    }, 5);
  }

  async stopCamera() {
    this.clearCameraStreamTracks();
    this.cameraStarted = false;
  }*/
}



